import React, { useState, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Building2,
  Target,
  FileText,
  Palette,
  Link as LinkIcon,
  Sparkles,
  LayoutGrid,
  Paperclip,
  CalendarClock,
  AlertTriangle,
  MessageSquare,
  ShieldCheck,
  ExternalLink,
  Mail,
  Instagram,
  Globe,
  Flag,
  Edit2,
  Send,
  RotateCcw,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import MultiImageUploader from '../components/MultiImageUploader';
import MultiFileUploader from '../components/MultiFileUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MARKETING_UPLOAD = `${API}/marketing-requests/upload`;
const DRAFT_KEY = 'dccf_marketing_request_draft_v1';

// ---------- Constants ----------
const PROJECT_TYPES = [
  'Flyer',
  'Social Media Post',
  'Campaign',
  'Event Promotion',
  'Branding',
  'Video / Reel',
  'Website Update',
  'Other',
];

const STYLE_DIRECTIONS = ['Elegant', 'Modern', 'Minimal', 'Premium', 'Urban', 'Corporate', 'Fun'];
const FORMAT_OUTPUTS = ['Post', 'Story', 'Reel', 'Print', 'Banner', 'Other'];

const PRIORITY_OPTIONS = [
  { value: 'standard', label: 'Standard', helper: 'Normal queue · 48h review' },
  { value: 'high', label: 'High', helper: 'Elevated priority, tighter timeline' },
  { value: 'urgent', label: 'Urgent', helper: 'Requires Eternity Media approval' },
];

// ---------- Initial form ----------
const initialForm = {
  // 1
  company_name: '', contact_name: '', email: '', phone_whatsapp: '',
  social_handles: '', website: '',
  // 2
  project_types: [], project_type_other: '',
  // 3
  objective: '', desired_action: '', target_audience: '',
  // 4
  content_title: '', content_subtitle: '', content_text: '',
  event_date: '', event_time: '', event_location: '', price: '', promotions: '',
  // 5
  has_logo: '', logo_files: [], brand_colors: '', typography: '', brand_guidelines: [],
  // 6
  reference_links: '', reference_images: [], style_description: '', competitors: '',
  // 7
  style_directions: [],
  // 8
  format_outputs: [], format_other: '',
  // 9
  material_files: [],
  // 10
  due_date: '', is_urgent: false,
  // 11
  priority: 'standard',
  // 12
  final_notes: '',
};

// ---------- Step definitions ----------
const STEPS = [
  { key: 'business',   icon: Building2,      title: "Let's start with your business",   helper: 'Tell us who this request is for and how to reach you.' },
  { key: 'project',    icon: LayoutGrid,     title: 'What kind of project is this?',    helper: 'Pick every deliverable that applies.' },
  { key: 'objective',  icon: Target,         title: "What's the goal?",                 helper: 'Why this project — and what should people do next.' },
  { key: 'content',    icon: FileText,       title: 'Tell us about your content',       helper: 'Exact text, dates, and offer details.' },
  { key: 'brand',      icon: Palette,        title: 'Share your brand identity',        helper: 'Logo, colors, typography, guidelines.' },
  { key: 'references', icon: LinkIcon,       title: 'Inspiration & references',         helper: 'Links, images, and competitors for context.' },
  { key: 'style',      icon: Sparkles,       title: 'Choose your style',                helper: 'Pick the visual directions that fit.' },
  { key: 'format',     icon: LayoutGrid,     title: 'Final formats',                    helper: 'How should we deliver the work?' },
  { key: 'files',      icon: Paperclip,      title: 'Add your files',                   helper: 'Everything that should go into the final piece.' },
  { key: 'deadline',   icon: CalendarClock,  title: 'When do you need this?',           helper: 'Pick a realistic due date.' },
  { key: 'priority',   icon: AlertTriangle,  title: 'How urgent is this?',              helper: 'Helps us route the request correctly.' },
  { key: 'notes',      icon: MessageSquare,  title: 'Anything else?',                   helper: 'Optional final notes for the team.' },
  { key: 'review',     icon: Check,          title: 'Review & submit',                  helper: 'Confirm your brief and acknowledge the disclaimer.' },
];
const TOTAL_STEPS = STEPS.length;

// ---------- Helpers ----------
const FieldLabel = ({ children, required, hint }) => (
  <div className="mb-1.5">
    <Label className="text-[14px] font-medium text-slate-800">
      {children} {required && <span className="text-[#E63946]">*</span>}
    </Label>
    {hint && <p className="text-[12px] text-slate-500 mt-0.5">{hint}</p>}
  </div>
);

const InlineError = ({ error }) =>
  error ? (
    <p className="text-[12px] text-rose-600 mt-1 flex items-center gap-1">
      <AlertTriangle className="w-3 h-3" /> {error}
    </p>
  ) : null;

// Stable module-level field component. Crucial: defined OUTSIDE any parent so
// React never remounts the <input> on keystrokes (was causing letter-by-letter
// focus loss on mobile).
const TextField = React.memo(function TextField({
  name, value, onChange, onBlur, error, type = 'text', inputMode,
  placeholder, required, autoComplete,
}) {
  return (
    <>
      <Input
        name={name}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        value={value || ''}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={placeholder}
        required={required}
        className={`h-12 rounded-xl text-[16px] px-4 w-full ${error ? 'border-rose-400 focus-visible:ring-rose-400' : ''}`}
        data-testid={`mr-${name.replace(/_/g, '-')}`}
      />
      <InlineError error={error} />
    </>
  );
});

// Large, thumb-friendly pill (min-height 44px)
const Pill = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`min-h-[44px] px-4 py-2.5 rounded-full text-[14px] font-medium border transition-all active:scale-[0.97] ${
      active
        ? 'bg-[#002249] text-white border-[#002249] shadow-sm'
        : 'bg-white text-slate-700 border-slate-300 hover:border-[#002249]/60'
    }`}
  >
    {active && <Check className="w-4 h-4 inline-block -mt-0.5 mr-1" />}
    {children}
  </button>
);

// ---------- Step validator ----------
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateStep = (stepIdx, form) => {
  const errs = {};
  const req = (k, msg) => {
    const v = form[k];
    const empty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
    if (empty) errs[k] = msg;
  };
  switch (STEPS[stepIdx].key) {
    case 'business':
      req('company_name', 'Company name is required');
      req('contact_name', 'Contact name is required');
      if (!form.email) errs.email = 'Email is required';
      else if (!emailRe.test(form.email)) errs.email = 'Enter a valid email';
      req('phone_whatsapp', 'Phone / WhatsApp is required');
      break;
    case 'project':
      req('project_types', 'Select at least one project type');
      break;
    case 'objective':
      req('objective', 'Tell us the goal');
      req('desired_action', 'What action should users take?');
      req('target_audience', 'Who is the audience?');
      break;
    case 'content':
      req('content_text', 'Caption or full text is required');
      break;
    case 'brand':
      req('has_logo', 'Select Yes or No');
      break;
    case 'deadline':
      req('due_date', 'Pick a due date');
      break;
    case 'priority':
      req('priority', 'Choose a priority level');
      break;
    // Other steps are optional
    default:
      break;
  }
  return errs;
};

// ==============================================================
// MAIN COMPONENT
// ==============================================================
const MarketingRequestPage = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [touched, setTouched] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [restored, setRestored] = useState(false);
  const scrollRef = useRef(null);

  // ------ Auto-save + restore ------
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.form) {
          setForm({ ...initialForm, ...parsed.form });
          setAcknowledged(!!parsed.acknowledged);
          setStep(Math.min(parsed.step || 0, TOTAL_STEPS - 1));
          setRestored(true);
        }
      }
    } catch {
      /* ignore malformed draft */
    }
  }, []);

  useEffect(() => {
    if (submitted) return;
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, acknowledged, step }));
    } catch {
      /* quota or disabled — ignore */
    }
  }, [form, acknowledged, step, submitted]);

  const clearDraft = () => {
    try { localStorage.removeItem(DRAFT_KEY); } catch {}
  };

  // ------ Setters ------
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const markTouched = (k) => setTouched((p) => ({ ...p, [k]: true }));

  const toggleInArray = (k, item) =>
    setForm((p) => {
      const arr = p[k] || [];
      return { ...p, [k]: arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item] };
    });

  // ------ Navigation ------
  const stepErrors = useMemo(() => validateStep(step, form), [step, form]);
  const hasStepErrors = Object.keys(stepErrors).length > 0;

  const goToStep = (next) => {
    const clamped = Math.max(0, Math.min(TOTAL_STEPS - 1, next));
    setStep(clamped);
    // Scroll to top of form area on step change
    setTimeout(() => {
      scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleNext = () => {
    if (hasStepErrors) {
      // Mark all fields in error as touched so they show inline
      setTouched((p) => ({ ...p, ...Object.keys(stepErrors).reduce((acc, k) => ({ ...acc, [k]: true }), {}) }));
      toast.error('Please complete the highlighted fields');
      return;
    }
    goToStep(step + 1);
  };

  const handleBack = () => goToStep(step - 1);
  const jumpToStep = (idx) => goToStep(idx);

  const resetDraft = () => {
    if (!window.confirm('Start over? Your saved draft will be cleared.')) return;
    clearDraft();
    setForm(initialForm);
    setAcknowledged(false);
    setTouched({});
    setStep(0);
    setRestored(false);
    toast.success('Draft cleared');
  };

  // ------ Submit ------
  const finalValidationErrors = useMemo(() => {
    const errs = [];
    for (let i = 0; i < TOTAL_STEPS - 1; i++) {
      const e = validateStep(i, form);
      if (Object.keys(e).length) errs.push({ step: i, fields: Object.keys(e) });
    }
    if (!acknowledged) errs.push({ step: TOTAL_STEPS - 1, fields: ['acknowledged'] });
    return errs;
  }, [form, acknowledged]);

  const handleSubmit = async () => {
    if (finalValidationErrors.length > 0) {
      const first = finalValidationErrors[0];
      goToStep(first.step);
      toast.error('Some required fields are missing');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/marketing-requests`, {
        request_type: 'pro_v1',
        status: 'pending_review',
        data: {
          ...form,
          disclaimer_acknowledged: true,
          disclaimer_acknowledged_at: new Date().toISOString(),
          version: 'pro_v1',
        },
      });
      clearDraft();
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ------ Success screen takes over ------
  if (submitted) {
    return <SuccessScreen onReset={() => {
      setForm(initialForm);
      setAcknowledged(false);
      setTouched({});
      setStep(0);
      setSubmitted(false);
    }} />;
  }

  const current = STEPS[step];
  const progressPct = ((step + 1) / TOTAL_STEPS) * 100;
  const isReview = current.key === 'review';

  return (
    <div className="min-h-screen pt-16 sm:pt-20 bg-[#F7F6F2]" data-testid="marketing-request-page">
      {/* Sticky progress header */}
      <div className="sticky top-16 sm:top-20 z-30 bg-[#F7F6F2]/95 backdrop-blur-md border-b border-slate-200" data-testid="mr-progress-header">
        <div className="container-custom max-w-3xl px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] sm:text-xs font-bold text-[#E63946] tracking-[0.18em] uppercase">
              Marketing Request
            </span>
            <span className="text-[12px] sm:text-sm font-semibold text-slate-700" data-testid="mr-step-indicator">
              Step {step + 1} of {TOTAL_STEPS}
            </span>
          </div>
          <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
            <motion.div
              className="h-full bg-[#002249]"
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={{ type: 'spring', stiffness: 180, damping: 24 }}
              data-testid="mr-progress-bar"
            />
          </div>
          {restored && !submitted && step === 0 && (
            <div className="mt-3 flex items-center justify-between gap-3 p-2.5 rounded-lg bg-[#002249]/5 border border-[#002249]/15 text-[12px]">
              <span className="text-[#002249] font-medium flex items-center gap-1.5">
                <RotateCcw className="w-3.5 h-3.5" /> Draft restored — keep going or start over.
              </span>
              <button
                type="button"
                onClick={resetDraft}
                className="text-[12px] font-semibold text-rose-600 hover:underline"
                data-testid="mr-clear-draft"
              >
                Start over
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Step content */}
      <div ref={scrollRef} className="pt-4 sm:pt-6 pb-36 sm:pb-32">
        <div className="container-custom max-w-3xl px-4 sm:px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.key}
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm"
              data-testid={`mr-step-${current.key}`}
            >
              {/* Step header */}
              <div className="p-4 sm:p-7 border-b border-slate-100">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-[#002249] text-white flex items-center justify-center">
                    {React.createElement(current.icon, { className: 'w-5 h-5 sm:w-6 sm:h-6' })}
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <h2 className="text-[18px] sm:text-2xl font-bold text-[#002249] leading-tight">
                      {current.title}
                    </h2>
                    <p className="text-[13px] sm:text-[14px] text-slate-500 mt-1 leading-relaxed">
                      {current.helper}
                    </p>
                  </div>
                </div>
              </div>

              {/* Step body */}
              <div className="p-4 sm:p-7">
                <StepBody
                  stepKey={current.key}
                  form={form}
                  set={set}
                  markTouched={markTouched}
                  touched={touched}
                  stepErrors={stepErrors}
                  toggleInArray={toggleInArray}
                  jumpToStep={jumpToStep}
                  acknowledged={acknowledged}
                  setAcknowledged={setAcknowledged}
                />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.04)] safe-area-bottom">
        <div className="container-custom max-w-3xl px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-stretch gap-2.5 sm:gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleBack}
              disabled={step === 0}
              className="h-12 min-w-[92px] sm:min-w-[112px] px-4 rounded-xl border-slate-300 disabled:opacity-40"
              data-testid="mr-back-btn"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Back</span>
            </Button>

            {isReview ? (
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || finalValidationErrors.length > 0}
                className="flex-1 h-12 rounded-xl bg-[#002249] hover:bg-[#0a3570] text-[15px] font-semibold disabled:opacity-60"
                data-testid="mr-submit-btn"
              >
                {isSubmitting ? 'Submitting...' : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleNext}
                className="flex-1 h-12 rounded-xl bg-[#002249] hover:bg-[#0a3570] text-[15px] font-semibold"
                data-testid="mr-next-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* About Eternity Media — visible on step 0 only to keep the wizard focused on mobile */}
      {step === 0 && <AboutEternityMedia compact />}
    </div>
  );
};

// ==============================================================
// Step body — renders UI specific to the current step
// ==============================================================
const StepBody = ({
  stepKey, form, set, markTouched, touched, stepErrors, toggleInArray, jumpToStep,
  acknowledged, setAcknowledged,
}) => {
  // IMPORTANT: `TextInput` must NOT be a component defined here — doing so
  // would give it a new identity on every keystroke and React would unmount
  // the <input>, causing focus loss on mobile. Use the stable module-level
  // `TextField` directly with this prop-builder helper instead.
  const tfProps = (name, extras = {}) => {
    const err = touched[name] ? stepErrors[name] : null;
    return {
      name,
      value: form[name],
      onChange: (e) => set(name, e.target.value),
      onBlur: () => markTouched(name),
      error: err,
      ...extras,
    };
  };

  // Helper to show error for Textarea fields (same logic as tfProps)
  const showErr = (name) => touched[name] ? stepErrors[name] : null;

  switch (stepKey) {
    // ---------- 1. BUSINESS ----------
    case 'business':
      return (
        <div className="space-y-4 sm:space-y-5">
          <div>
            <FieldLabel required>Company name</FieldLabel>
            <TextField {...tfProps("company_name", { autoComplete: "organization", required: true })} />
          </div>
          <div>
            <FieldLabel required>Your name</FieldLabel>
            <TextField {...tfProps("contact_name", { autoComplete: "name", required: true })} />
          </div>
          <div>
            <FieldLabel required>Email</FieldLabel>
            <TextField {...tfProps("email", { type: "email", inputMode: "email", autoComplete: "email", placeholder: "you@company.com", required: true })} />
          </div>
          <div>
            <FieldLabel required>Phone / WhatsApp</FieldLabel>
            <TextField {...tfProps("phone_whatsapp", { type: "tel", inputMode: "tel", autoComplete: "tel", placeholder: "+1 555 000 0000", required: true })} />
          </div>
          <div>
            <FieldLabel hint="e.g. @yourbrand on IG / FB">Social handles</FieldLabel>
            <TextField {...tfProps("social_handles", { placeholder: "@yourbrand" })} />
          </div>
          <div>
            <FieldLabel>Website</FieldLabel>
            <TextField {...tfProps("website", { type: "url", inputMode: "url", placeholder: "https://..." })} />
          </div>
        </div>
      );

    // ---------- 2. PROJECT TYPE ----------
    case 'project':
      return (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {PROJECT_TYPES.map((t) => (
              <Pill
                key={t}
                active={form.project_types.includes(t)}
                onClick={() => toggleInArray('project_types', t)}
                testid={`mr-project-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                {t}
              </Pill>
            ))}
          </div>
          {stepErrors.project_types && touched.project_types && (
            <p className="text-[13px] text-rose-600">Select at least one project type.</p>
          )}
          {form.project_types.includes('Other') && (
            <div>
              <FieldLabel>Describe "Other"</FieldLabel>
              <TextField {...tfProps("project_type_other", { placeholder: "e.g. Podcast graphics" })} />
            </div>
          )}
        </div>
      );

    // ---------- 3. OBJECTIVE ----------
    case 'objective':
      return (
        <div className="space-y-4 sm:space-y-5">
          <div>
            <FieldLabel required hint="What do you want this request to accomplish?">Goal of this request</FieldLabel>
            <Textarea
              value={form.objective}
              onChange={(e) => set('objective', e.target.value)}
              onBlur={() => markTouched('objective')}
              rows={4}
              className={`rounded-xl text-[16px] p-4 ${showErr('objective') ? 'border-rose-400 focus-visible:ring-rose-400' : ''}`}
              placeholder="e.g. Drive RSVPs to our launch event"
              data-testid="mr-objective"
            />
            <InlineError error={showErr('objective')} />
          </div>
          <div>
            <FieldLabel required hint="The one action users should take">Desired action</FieldLabel>
            <TextField {...tfProps("desired_action", { placeholder: "Register · Visit · Call · Buy...", required: true })} />
          </div>
          <div>
            <FieldLabel required hint="Demographic, city, interest">Target audience</FieldLabel>
            <TextField {...tfProps("target_audience", { placeholder: "Dominican professionals in South FL", required: true })} />
          </div>
        </div>
      );

    // ---------- 4. CONTENT ----------
    case 'content':
      return (
        <div className="space-y-4 sm:space-y-5">
          <div>
            <FieldLabel>Title</FieldLabel>
            <TextField {...tfProps("content_title", { placeholder: "Short headline" })} />
          </div>
          <div>
            <FieldLabel>Subtitle</FieldLabel>
            <TextField {...tfProps("content_subtitle")} />
          </div>
          <div>
            <FieldLabel required hint="Copy you want featured — caption, body, or event description">Full text / caption</FieldLabel>
            <Textarea
              value={form.content_text}
              onChange={(e) => set('content_text', e.target.value)}
              onBlur={() => markTouched('content_text')}
              rows={5}
              className={`rounded-xl text-[16px] p-4 ${showErr('content_text') ? 'border-rose-400 focus-visible:ring-rose-400' : ''}`}
              data-testid="mr-content-text"
            />
            <InlineError error={showErr('content_text')} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-3">
            <div>
              <FieldLabel>Event date</FieldLabel>
              <TextField {...tfProps("event_date", { type: "date" })} />
            </div>
            <div>
              <FieldLabel>Event time</FieldLabel>
              <TextField {...tfProps("event_time", { placeholder: "6:00 PM" })} />
            </div>
            <div>
              <FieldLabel>Location</FieldLabel>
              <TextField {...tfProps("event_location", { placeholder: "Venue or city" })} />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-3">
            <div>
              <FieldLabel>Price</FieldLabel>
              <TextField {...tfProps("price", { placeholder: "Free · $25" })} />
            </div>
            <div>
              <FieldLabel>Promotions</FieldLabel>
              <TextField {...tfProps("promotions", { placeholder: "Early bird 20% off" })} />
            </div>
          </div>
        </div>
      );

    // ---------- 5. BRAND ----------
    case 'brand':
      return (
        <div className="space-y-4 sm:space-y-5">
          <div>
            <FieldLabel required>Do you have a logo?</FieldLabel>
            <RadioGroup
              value={form.has_logo}
              onValueChange={(v) => { set('has_logo', v); markTouched('has_logo'); }}
              className="grid grid-cols-2 gap-3"
            >
              {[
                { v: 'yes', label: 'Yes, I have one' },
                { v: 'no', label: 'No, not yet' },
              ].map((opt) => (
                <label
                  key={opt.v}
                  htmlFor={`has_logo_${opt.v}`}
                  className={`cursor-pointer min-h-[56px] rounded-xl border px-4 py-3 flex items-center gap-3 transition-all active:scale-[0.98] ${
                    form.has_logo === opt.v
                      ? 'border-[#002249] bg-[#002249]/5 ring-2 ring-[#002249]/20'
                      : 'border-slate-200 hover:border-[#002249]/40'
                  }`}
                  data-testid={`mr-has-logo-${opt.v}`}
                >
                  <RadioGroupItem value={opt.v} id={`has_logo_${opt.v}`} />
                  <span className="text-[15px] font-medium text-slate-800">{opt.label}</span>
                </label>
              ))}
            </RadioGroup>
            <InlineError error={showErr('has_logo')} />
          </div>

          {form.has_logo === 'yes' && (
            <div>
              <FieldLabel hint="PNG, JPG, SVG, or PDF · up to 3 files · 10 MB each">Upload logo</FieldLabel>
              <MultiFileUploader
                value={form.logo_files}
                onChange={(v) => set('logo_files', v)}
                max={3}
                maxMB={10}
                label="Upload Logo"
                helper="PNG · JPG · SVG · PDF · up to 3 files"
                accept="image/*,application/pdf,.svg"
                dataTestId="mr-logo-uploader"
              />
            </div>
          )}

          <div>
            <FieldLabel hint="e.g. Navy #002249, Red #E63946">Brand colors</FieldLabel>
            <TextField {...tfProps("brand_colors")} />
          </div>
          <div>
            <FieldLabel hint="e.g. Montserrat, Playfair">Typography</FieldLabel>
            <TextField {...tfProps("typography")} />
          </div>
          <div>
            <FieldLabel>Brand guidelines (optional)</FieldLabel>
            <MultiFileUploader
              value={form.brand_guidelines}
              onChange={(v) => set('brand_guidelines', v)}
              max={3}
              maxMB={10}
              label="Upload Guidelines"
              helper="PDF · DOC · PNG · JPG"
              accept="image/*,application/pdf,.doc,.docx"
              dataTestId="mr-brand-guidelines"
            />
          </div>
        </div>
      );

    // ---------- 6. REFERENCES ----------
    case 'references':
      return (
        <div className="space-y-4 sm:space-y-5">
          <div>
            <FieldLabel hint="Paste URLs, one per line">Reference links</FieldLabel>
            <Textarea
              value={form.reference_links}
              onChange={(e) => set('reference_links', e.target.value)}
              rows={3}
              placeholder="https://..."
              className="rounded-xl text-[16px] p-4"
              data-testid="mr-reference-links"
            />
          </div>
          <div>
            <FieldLabel>Reference images</FieldLabel>
            <MultiImageUploader
              value={form.reference_images}
              onChange={(v) => set('reference_images', v)}
              max={8}
              endpoint={MARKETING_UPLOAD}
              label="Add Reference Images"
              dataTestId="mr-reference-images"
            />
          </div>
          <div>
            <FieldLabel hint="In your own words">What style do you like?</FieldLabel>
            <TextField {...tfProps("style_description")} />
          </div>
          <div>
            <FieldLabel>Competitors (optional)</FieldLabel>
            <TextField {...tfProps("competitors")} />
          </div>
        </div>
      );

    // ---------- 7. STYLE ----------
    case 'style':
      return (
        <div className="flex flex-wrap gap-2">
          {STYLE_DIRECTIONS.map((s) => (
            <Pill
              key={s}
              active={form.style_directions.includes(s)}
              onClick={() => toggleInArray('style_directions', s)}
              testid={`mr-style-${s.toLowerCase()}`}
            >
              {s}
            </Pill>
          ))}
        </div>
      );

    // ---------- 8. FORMAT ----------
    case 'format':
      return (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {FORMAT_OUTPUTS.map((fmt) => (
              <Pill
                key={fmt}
                active={form.format_outputs.includes(fmt)}
                onClick={() => toggleInArray('format_outputs', fmt)}
                testid={`mr-format-${fmt.toLowerCase()}`}
              >
                {fmt}
              </Pill>
            ))}
          </div>
          {form.format_outputs.includes('Other') && (
            <div>
              <FieldLabel>Describe "Other"</FieldLabel>
              <TextField {...tfProps("format_other")} />
            </div>
          )}
        </div>
      );

    // ---------- 9. FILES ----------
    case 'files':
      return (
        <MultiFileUploader
          value={form.material_files}
          onChange={(v) => set('material_files', v)}
          max={10}
          maxMB={10}
          label="Tap to upload files"
          helper="Images, videos, PDFs, QR codes — up to 10 files · 10 MB each"
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip"
          dataTestId="mr-material-files"
        />
      );

    // ---------- 10. DEADLINE ----------
    case 'deadline':
      return (
        <div className="space-y-4">
          <div>
            <FieldLabel required>Due date</FieldLabel>
            <TextField {...tfProps("due_date", { type: "date", required: true })} />
          </div>
          <label
            htmlFor="mr-is-urgent"
            className={`flex items-center gap-3 cursor-pointer min-h-[56px] rounded-xl border px-4 py-3 transition-all active:scale-[0.99] ${
              form.is_urgent ? 'border-rose-400 bg-rose-50' : 'border-slate-200 hover:border-slate-300'
            }`}
            data-testid="mr-is-urgent-label"
          >
            <Checkbox
              id="mr-is-urgent"
              checked={form.is_urgent}
              onCheckedChange={(v) => set('is_urgent', !!v)}
              data-testid="mr-is-urgent"
            />
            <span className="flex-1 text-[15px] font-medium text-slate-800">This is urgent</span>
            {form.is_urgent && <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0" />}
          </label>
        </div>
      );

    // ---------- 11. PRIORITY ----------
    case 'priority':
      return (
        <RadioGroup
          value={form.priority}
          onValueChange={(v) => set('priority', v)}
          className="space-y-2.5"
        >
          {PRIORITY_OPTIONS.map((p) => (
            <label
              key={p.value}
              htmlFor={`prio-${p.value}`}
              className={`cursor-pointer rounded-xl border p-4 flex items-start gap-3 transition-all active:scale-[0.99] ${
                form.priority === p.value
                  ? 'border-[#002249] bg-[#002249]/5 ring-2 ring-[#002249]/20 shadow-sm'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
              data-testid={`mr-priority-${p.value}`}
            >
              <RadioGroupItem value={p.value} id={`prio-${p.value}`} className="mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold text-[#002249] leading-tight">{p.label}</p>
                <p className="text-[13px] text-slate-500 mt-1 leading-relaxed">{p.helper}</p>
              </div>
              {p.value === 'urgent' && <Flag className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />}
            </label>
          ))}
        </RadioGroup>
      );

    // ---------- 12. NOTES ----------
    case 'notes':
      return (
        <Textarea
          value={form.final_notes}
          onChange={(e) => set('final_notes', e.target.value)}
          rows={6}
          placeholder="Any preferences, warnings, or extra context..."
          className="rounded-xl text-[16px] p-4"
          data-testid="mr-final-notes"
        />
      );

    // ---------- 13. REVIEW ----------
    case 'review':
      return (
        <ReviewSummary
          form={form}
          jumpToStep={jumpToStep}
          acknowledged={acknowledged}
          setAcknowledged={setAcknowledged}
        />
      );

    default:
      return null;
  }
};

// ==============================================================
// Review summary
// ==============================================================
const SummaryRow = ({ label, value, stepIdx, jumpToStep, testid }) => {
  const empty =
    value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);
  return (
    <div className="flex items-start justify-between gap-3 py-3 border-b border-slate-100 last:border-b-0" data-testid={testid}>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">{label}</p>
        <div className="mt-0.5">
          {empty ? (
            <span className="text-[13px] text-slate-400">—</span>
          ) : Array.isArray(value) ? (
            <div className="flex flex-wrap gap-1.5">
              {value.map((v, i) => (
                <span key={i} className="px-2 py-0.5 rounded-full bg-slate-100 text-[12px] text-slate-700">
                  {typeof v === 'object' ? v.filename || 'file' : String(v)}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-[14px] text-slate-800 break-words">{String(value)}</span>
          )}
        </div>
      </div>
      {jumpToStep && (
        <button
          type="button"
          onClick={() => jumpToStep(stepIdx)}
          className="flex-shrink-0 w-8 h-8 rounded-md text-slate-400 hover:text-[#002249] hover:bg-slate-100 flex items-center justify-center"
          aria-label="Edit"
          data-testid={`${testid}-edit`}
        >
          <Edit2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
};

const ReviewSummary = ({ form, jumpToStep, acknowledged, setAcknowledged }) => {
  const totalFiles =
    (form.logo_files?.length || 0) +
    (form.brand_guidelines?.length || 0) +
    (form.reference_images?.length || 0) +
    (form.material_files?.length || 0);

  return (
    <div className="space-y-5" data-testid="mr-review-summary">
      {/* Hero summary card */}
      <div className="rounded-xl bg-[#002249] text-white p-5">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#E63946] mb-1">
          Brief Overview
        </p>
        <h3 className="text-[18px] font-bold leading-tight">{form.company_name || 'Your request'}</h3>
        <p className="text-[13px] text-white/75 mt-1 line-clamp-2">{form.objective || 'No objective provided'}</p>
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="bg-white/10 rounded-lg p-2.5">
            <p className="text-[10px] uppercase text-white/60 tracking-wide">Deadline</p>
            <p className="text-[13px] font-semibold mt-0.5 truncate">{form.due_date || '—'}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-2.5">
            <p className="text-[10px] uppercase text-white/60 tracking-wide">Priority</p>
            <p className="text-[13px] font-semibold mt-0.5 capitalize">{form.priority}</p>
          </div>
          <div className="bg-white/10 rounded-lg p-2.5">
            <p className="text-[10px] uppercase text-white/60 tracking-wide">Files</p>
            <p className="text-[13px] font-semibold mt-0.5">{totalFiles}</p>
          </div>
        </div>
      </div>

      {/* Field groups */}
      <div className="rounded-xl border border-slate-200 bg-white px-4">
        <SummaryRow label="Company" value={form.company_name} stepIdx={0} jumpToStep={jumpToStep} testid="sum-company" />
        <SummaryRow label="Contact" value={form.contact_name} stepIdx={0} jumpToStep={jumpToStep} testid="sum-contact" />
        <SummaryRow label="Email" value={form.email} stepIdx={0} jumpToStep={jumpToStep} testid="sum-email" />
        <SummaryRow label="Phone" value={form.phone_whatsapp} stepIdx={0} jumpToStep={jumpToStep} testid="sum-phone" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white px-4">
        <SummaryRow label="Project Types" value={form.project_types} stepIdx={1} jumpToStep={jumpToStep} testid="sum-project" />
        <SummaryRow label="Desired Action" value={form.desired_action} stepIdx={2} jumpToStep={jumpToStep} testid="sum-action" />
        <SummaryRow label="Target Audience" value={form.target_audience} stepIdx={2} jumpToStep={jumpToStep} testid="sum-audience" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white px-4">
        <SummaryRow label="Style Direction" value={form.style_directions} stepIdx={6} jumpToStep={jumpToStep} testid="sum-style" />
        <SummaryRow label="Formats" value={form.format_outputs} stepIdx={7} jumpToStep={jumpToStep} testid="sum-format" />
      </div>
      <div className="rounded-xl border border-slate-200 bg-white px-4">
        <SummaryRow label="Attached Files" value={totalFiles > 0 ? [...form.logo_files, ...form.brand_guidelines, ...form.reference_images, ...form.material_files] : []} stepIdx={8} jumpToStep={jumpToStep} testid="sum-files" />
      </div>

      {/* Disclaimer */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-5" data-testid="marketing-disclaimer">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-[16px] font-bold text-amber-900">Marketing Request Disclaimer</h3>
          </div>
        </div>
        <ul className="list-disc pl-5 space-y-1.5 text-[13px] text-amber-900/90 leading-relaxed">
          <li>Incomplete submissions will delay the project timeline.</li>
          <li>A minimum of 48 hours is required for initial review and evaluation.</li>
          <li>
            After review, the request will be:
            <ul className="list-[circle] pl-5 mt-1 space-y-0.5">
              <li>Approved</li>
              <li>Returned for additional information</li>
              <li>Declined (if not aligned with brand or objectives)</li>
            </ul>
          </li>
          <li>Eternity Media reserves the right to maintain brand consistency and quality standards.</li>
          <li>Additional revisions or changes outside the original scope may affect delivery time.</li>
        </ul>
        <p className="text-[13px] text-amber-900/90 mt-3">
          By submitting this request, you agree to the process and timeline outlined above.
        </p>
        <label
          htmlFor="mr-ack"
          className={`mt-4 flex items-start gap-3 cursor-pointer min-h-[56px] rounded-xl border px-4 py-3 transition-all ${
            acknowledged ? 'border-amber-500 bg-white' : 'border-amber-300 bg-white/60'
          }`}
          data-testid="mr-disclaimer-acknowledge"
        >
          <Checkbox
            id="mr-ack"
            checked={acknowledged}
            onCheckedChange={(v) => setAcknowledged(!!v)}
            className="mt-0.5"
          />
          <span className="text-[14px] font-semibold text-amber-900 leading-snug">
            I acknowledge the Marketing Request Disclaimer and agree to the review process.
          </span>
        </label>
      </div>
    </div>
  );
};

// ==============================================================
// Success screen
// ==============================================================
const SuccessScreen = ({ onReset }) => (
  <div className="min-h-screen pt-16 sm:pt-20 bg-[#F7F6F2]" data-testid="marketing-success-screen">
    <div className="container-custom max-w-2xl px-4 sm:px-6 py-12 sm:py-16">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm"
      >
        <div className="bg-gradient-to-br from-[#002249] to-[#001833] px-6 sm:px-10 py-10 sm:py-14 text-center text-white relative overflow-hidden">
          <div
            className="absolute inset-0 opacity-20 pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 30% 30%, rgba(230,57,70,0.5) 0%, transparent 40%), radial-gradient(circle at 70% 70%, rgba(120,140,220,0.4) 0%, transparent 40%)',
            }}
            aria-hidden="true"
          />
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 220, damping: 18 }}
            className="relative mx-auto w-20 h-20 rounded-full bg-white/10 border-2 border-white/30 flex items-center justify-center mb-5"
          >
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          </motion.div>
          <h1 className="relative text-2xl sm:text-3xl font-bold leading-tight">
            Your request has been submitted
          </h1>
          <p className="relative text-white/75 text-[14px] sm:text-base mt-2 leading-relaxed">
            Thank you — Eternity Media has received your brief.
          </p>
        </div>
        <div className="p-6 sm:p-10">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#E63946] mb-3">
            What happens next
          </p>
          <ol className="space-y-4">
            {[
              { t: 'Initial review', d: 'We review your request within 48 hours.' },
              { t: 'Decision', d: 'Approved, returned for more info, or declined.' },
              { t: 'Production', d: 'If approved, we schedule the work and align on delivery.' },
            ].map((s, i) => (
              <li key={i} className="flex gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#002249] text-white text-[12px] font-bold flex items-center justify-center">
                  {i + 1}
                </div>
                <div>
                  <p className="text-[14px] font-semibold text-slate-900">{s.t}</p>
                  <p className="text-[13px] text-slate-600 mt-0.5">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-8 pt-6 border-t border-slate-200 flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onReset}
              variant="outline"
              className="h-12 rounded-xl flex-1"
              data-testid="mr-submit-another"
            >
              Submit another request
            </Button>
            <a href="/" className="flex-1">
              <Button className="h-12 rounded-xl w-full bg-[#002249] hover:bg-[#0a3570]" data-testid="mr-back-home">
                Back to home
              </Button>
            </a>
          </div>
        </div>
      </motion.div>
    </div>
    <AboutEternityMedia />
  </div>
);

// ==============================================================
// About Eternity Media — shared block (IG fixed to @eternitymp.us)
// ==============================================================
const AboutEternityMedia = ({ compact = false }) => (
  <section className={`relative ${compact ? 'py-10 sm:py-14' : 'py-14 sm:py-20'} bg-[#0E1221] text-white overflow-hidden`} data-testid="about-eternity-media">
    <div
      className="absolute inset-0 opacity-[0.04] pointer-events-none"
      style={{
        backgroundImage:
          'radial-gradient(circle at 20% 30%, rgba(230,57,70,0.5) 0%, transparent 45%), radial-gradient(circle at 80% 70%, rgba(120,140,220,0.4) 0%, transparent 45%)',
      }}
      aria-hidden="true"
    />
    <div className="container-custom max-w-3xl relative px-4 sm:px-6">
      <div className="text-center mb-8 sm:mb-10">
        <span className="text-[11px] sm:text-xs font-bold tracking-[0.22em] uppercase text-[#E63946]">
          About Our Marketing Team
        </span>
        <h2 className="text-[22px] sm:text-3xl lg:text-4xl font-bold mt-3 leading-tight">
          Brand-Led. Strategy-First. Built for Long-Term Authority.
        </h2>
      </div>

      <p className="text-white/80 text-[14px] sm:text-base leading-relaxed">
        All marketing, branding, and creative direction for the Dominican Chamber of Commerce of Florida is led by:
      </p>
      <p className="mt-4 text-[20px] sm:text-3xl font-extrabold tracking-[0.08em] text-white">
        ETERNITY MEDIA
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-x-10 gap-y-6">
        <div>
          <p className="text-[13px] sm:text-sm font-semibold text-white/90 mb-2">Eternity is responsible for:</p>
          <ul className="space-y-1.5 text-[13px] sm:text-sm text-white/75">
            <li>• Brand vision and positioning</li>
            <li>• Creative direction</li>
            <li>• Content strategy</li>
            <li>• Visual identity consistency</li>
            <li>• Campaign development</li>
          </ul>
        </div>
        <div>
          <p className="text-[13px] sm:text-sm font-semibold text-white/90 mb-2">Our approach combines:</p>
          <ul className="space-y-1.5 text-[13px] sm:text-sm text-white/75">
            <li>• Psychology</li>
            <li>• Branding architecture</li>
            <li>• Strategic content creation</li>
            <li>• High-level visual execution</li>
          </ul>
        </div>
      </div>

      <p className="mt-8 text-white/80 text-[14px] sm:text-base leading-relaxed">
        We do not create content randomly. We build structured brand systems designed for long-term growth, authority, and market positioning.
      </p>

      <div className="mt-10 pt-8 border-t border-white/10">
        <p className="text-[11px] sm:text-xs font-bold tracking-[0.22em] uppercase text-[#E63946] mb-4">
          Follow & Connect
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
          <a
            href="https://EternityMP.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-[14px] font-medium"
            data-testid="eternity-website"
          >
            <Globe className="w-4 h-4" />
            EternityMP.com
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </a>
          <a
            href="https://instagram.com/eternitymp.us"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-[14px] font-medium"
            data-testid="eternity-instagram"
          >
            <Instagram className="w-4 h-4" />
            @eternitymp.us
            <ExternalLink className="w-3.5 h-3.5 opacity-60" />
          </a>
          <a
            href="mailto:info@eternitymp.com"
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/10 hover:bg-white/15 border border-white/10 text-[14px] font-medium"
            data-testid="eternity-email"
          >
            <Mail className="w-4 h-4" />
            info@eternitymp.com
          </a>
        </div>
      </div>

      <p className="mt-8 text-white/55 text-[12px] sm:text-[13px] leading-relaxed italic">
        Our team works behind the scenes to ensure that every brand interaction is intentional, aligned, and impactful.
      </p>
    </div>
  </section>
);

export default MarketingRequestPage;
